import type { SearchPageFilterOption } from './sharedTypes';

export type NestedFilterOption = SearchPageFilterOption & {
  options?: NestedFilterOption[];
};

export const flattenFilterOptions = (options: NestedFilterOption[]): SearchPageFilterOption[] =>
  options.flatMap((option) =>
    Array.isArray(option.options) && option.options.length > 0
      ? flattenFilterOptions(option.options)
      : [{ label: option.label, value: option.value }]
  );

/**
 * Narrows static filter options against a criteria-level facet slice.
 *
 * Rules:
 * - `facet === undefined` → return `options` unchanged (fail-open).
 * - Keep an option iff `(facet[value] ?? 0) > 0` OR it is currently selected (pinned).
 * - `facet` defined-but-empty → only pinned selections remain (intentional).
 * - Nested groups: filter each group's `options`; drop groups left empty BY narrowing.
 *   Mirrors `flattenFilterOptions`: a node with an empty `options` array is treated as a
 *   selectable leaf (`length > 0` check), so the render surface never diverges from the
 *   sanitization surface.
 */
export const narrowFilterOptions = (
  options: NestedFilterOption[],
  facet: Record<string, number> | undefined,
  selectedValues: string[]
): NestedFilterOption[] => {
  if (facet === undefined) {
    return options;
  }

  return options.flatMap((option) => {
    if (Array.isArray(option.options) && option.options.length > 0) {
      const narrowedGroupOptions = narrowFilterOptions(option.options, facet, selectedValues);

      return narrowedGroupOptions.length > 0 ? [{ ...option, options: narrowedGroupOptions }] : [];
    }

    // Guard written as `(facet[value] ?? 0) > 0` — required for runtime correctness on sparse
    // records, independent of compiler settings.
    return (facet[option.value] ?? 0) > 0 || selectedValues.includes(option.value) ? [option] : [];
  });
};
